import { Button } from './Button'
import { StatusChip, type StatusTone } from './StatusChip'
import { DownloadIcon } from './icons'
import type { DownloadPhase } from '../lib/download-flow'

export interface DownloadCardProps {
  /** Deliverable name. */
  title: string
  /** Product · version · organization line. */
  subtitle: string
  statusTone: StatusTone
  statusLabel: string
  /** Current position in the button state machine. */
  phase: DownloadPhase
  /** Short explanation under the title (locked / reviewing reasons, errors). */
  notice?: string | null
  onReview: () => void
  onDownload: () => void
}

/**
 * One entitlement → deliverable card with the download button state machine:
 *   locked (no usable entitlement)
 *     → reviewing (acceptance needed — opens the Review & Accept modal)
 *     → ready → downloading → done (+ "download again")
 */
export function DownloadCard({
  title,
  subtitle,
  statusTone,
  statusLabel,
  phase,
  notice = null,
  onReview,
  onDownload,
}: DownloadCardProps) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-ground-2 px-6 py-5">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-body font-medium text-ink">{title}</p>
          <StatusChip tone={statusTone} label={statusLabel} />
        </div>
        <p className="text-small text-ink-muted">{subtitle}</p>
        {notice && <p className="text-small text-status-awaiting-fg">{notice}</p>}
      </div>
      <div className="flex items-center gap-3">
        {phase === 'reviewing' ? (
          <Button variant="primary" size="sm" onClick={onReview}>
            Review &amp; Accept
          </Button>
        ) : (
          <Button
            variant={phase === 'done' ? 'secondary' : 'primary'}
            size="sm"
            loading={phase === 'downloading'}
            disabled={phase === 'locked'}
            title={phase === 'locked' ? 'No active entitlement for this product' : undefined}
            onClick={onDownload}
          >
            <DownloadIcon width={16} height={16} aria-hidden="true" />
            {phase === 'done' ? 'Download again' : 'Download'}
          </Button>
        )}
      </div>
    </li>
  )
}
